import { z } from "zod";
import { ContractorType } from "@remi/enums/contractor-type";

/**
 * Operations Coordinator (OC) region roles
 */
const OCRegionRoleValues = [
  "REGIONAL_MANAGER",
  "ASSESSMENT_TASKS_OWNER",
  "CHANGE_ORDER_TASKS_OWNER",
  "PHOTO_REVIEW_TASKS_OWNER",
  "WORK_ORDER_TASKS_OWNER",
] as const;

/**
 * RLL-specific region roles
 */
export const RllRegionRoleValues = ["RLL_MANAGER", "MATERIALS_COORDINATOR", "PERMITTING_ADMIN"] as const;

/**
 * Contractor specific roles
 */
export const ContractorRoleValues = ["CORPORATE_PARTNER_POC"] as const;

/**
 * Use this if you need to mention a region role
 */
const MentionableRegionRoleValues = ["REGIONAL_MANAGER", ...RllRegionRoleValues, ...ContractorRoleValues] as const;

/**
 * Use this if you need to zod parse it
 */
const MentionableRegionRoleSchema = z.enum(MentionableRegionRoleValues);
type MentionableRegionRole = z.infer<typeof MentionableRegionRoleSchema>;

const MentionableRegionRoleDislayNames: Readonly<Record<MentionableRegionRole, string>> = {
  REGIONAL_MANAGER: "Regional Manager",
  RLL_MANAGER: "RLL Manager",
  MATERIALS_COORDINATOR: "Materials Coordinator",
  PERMITTING_ADMIN: "Permitting Admin",
  CORPORATE_PARTNER_POC: "CPM",
};

/**
 * All region roles
 * Use this if you need to loop through all the options
 */
const RegionRoleValues = [...OCRegionRoleValues, ...RllRegionRoleValues, ...ContractorRoleValues] as const;

/**
 * Use this if you need to zod parse it
 */
export const RegionRoleSchema = z.enum(RegionRoleValues);

/**
 * Use this if you need to use the literal value
 */
export const RegionRole = RegionRoleSchema.enum;

/**
 * Use this if you need to use the type
 */
export type RegionRole = z.infer<typeof RegionRoleSchema>;

export const NonInheritedRegionRoleValues: readonly RegionRole[] = [
  ...RllRegionRoleValues,
  ...ContractorRoleValues,
] as const satisfies RegionRole[];

// WORK_ORDER_TASKS_OWNER stays an OC project role but is sourced from the WORK_ORDER team, so a region
// must never supply it: two round robins naming one slot fails setProjectOwners, and a project holding
// only that slot would otherwise read as "region roles already assigned".
export const RegionSourcedOCRoleValues: readonly RegionRole[] = OCRegionRoleValues.filter(
  (role) => role !== RegionRole.WORK_ORDER_TASKS_OWNER,
);

export const RegionSourcedRegionRoleSchema = RegionRoleSchema.exclude([RegionRole.WORK_ORDER_TASKS_OWNER]);

export const RegionRoleDisplayNames: Readonly<Record<RegionRole, string>> = {
  ...MentionableRegionRoleDislayNames,
  // OC Roles
  ASSESSMENT_TASKS_OWNER: "Assessment Tasks Owner",
  CHANGE_ORDER_TASKS_OWNER: "Change Order Tasks Owner",
  PHOTO_REVIEW_TASKS_OWNER: "Photo Review Tasks Owner",
  WORK_ORDER_TASKS_OWNER: "WO specialist",
};

/**
 * Schema for creating OC (Operations Coordinator) regions
 */
export const CreateOCRegionSchema = z.object({
  name: z.string(),
  type: z.enum([ContractorType.REMI_PRO_CONTRACTOR, ContractorType.REMI_TEAM_CONTRACTOR]),
  regionalManagerIds: z.array(z.string()),
  assessmentTaskOwnerIds: z.array(z.string()),
  changeOrderTasksOwnerIds: z.array(z.string()),
  corporatePartnerPointOfContactIds: z.array(z.string()),
  photoReviewOwnerIds: z.array(z.string()),
});

/**
 * Schema for creating RLL (Remi Licensed Labor) regions
 */
export const CreateRllRegionSchema = z.object({
  name: z.string(),
  type: z.literal(ContractorType.REMI_LICENSED_LABOR),
  rllManagerIds: z.array(z.string()),
  materialsCoordinatorIds: z.array(z.string()),
  permittingAdminIds: z.array(z.string()),
});

/**
 * Combined schema for creating any type of region
 */
export const CreateRegionSchema = z.discriminatedUnion("type", [CreateOCRegionSchema, CreateRllRegionSchema]);
